import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquare, Settings, History, FileText, Send } from 'lucide-react';
import { WhatsAppConfigForm } from '@/components/admin/whatsapp/WhatsAppConfigForm';
import { WhatsAppSendMessage } from '@/components/admin/whatsapp/WhatsAppSendMessage';
import { WhatsAppHistory } from '@/components/admin/whatsapp/WhatsAppHistory';
import { WhatsAppTemplates } from '@/components/admin/whatsapp/WhatsAppTemplates';
import { AdminGuard } from '@/components/admin/AdminGuard';
import { useToast } from '@/components/ui/use-toast';

export default function WhatsAppPage() {
  const [activeTab, setActiveTab] = useState('send');
  const [isConfigured, setIsConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    checkConfiguration();
  }, []);

  const checkConfiguration = async () => {
    try {
      const response = await fetch('/api/admin/whatsapp/config');
      const data = await response.json();
      
      if (data.success) {
        setIsConfigured(data.data.isConfigured);
      }
    } catch (error) {
      console.error('Error checking WhatsApp configuration:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfigurationSaved = () => {
    setIsConfigured(true);
    toast({
      title: "Configuração salva",
      description: "API WhatsApp configurada com sucesso!",
    });
    setActiveTab('send');
  };

  const handleConfigurationRemoved = () => {
    setIsConfigured(false);
    toast({
      title: "Configuração removida",
      description: "Configuração da API WhatsApp foi removida.",
    });
    setActiveTab('config');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AdminGuard>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Mensagens WhatsApp</h1>
            <p className="text-foreground">Gerencie o envio de mensagens WhatsApp</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`h-2 w-2 rounded-full ${isConfigured ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm text-foreground">
              {isConfigured ? 'API Configurada' : 'API Não Configurada'}
            </span>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="config" className="flex items-center space-x-2">
              <Settings className="h-4 w-4" />
              <span>Configuração</span>
            </TabsTrigger>
            <TabsTrigger value="send" className="flex items-center space-x-2" disabled={!isConfigured}>
              <Send className="h-4 w-4" />
              <span>Enviar Mensagem</span>
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center space-x-2" disabled={!isConfigured}>
              <FileText className="h-4 w-4" />
              <span>Templates</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center space-x-2" disabled={!isConfigured}>
              <History className="h-4 w-4" />
              <span>Histórico</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="config" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Settings className="h-5 w-5" />
                  <span>Configuração da API WhatsApp</span>
                </CardTitle>
                <CardDescription>
                  Configure a integração com a API SimpleSign para envio de mensagens WhatsApp
                </CardDescription>
              </CardHeader>
              <CardContent>
                <WhatsAppConfigForm 
                  onConfigurationSaved={handleConfigurationSaved}
                  onConfigurationRemoved={handleConfigurationRemoved}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="send" className="space-y-6">
            {!isConfigured ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      API Não Configurada
                    </h3>
                    <p className="text-foreground mb-4">
                      Configure a API WhatsApp na aba de configurações para começar a enviar mensagens.
                    </p>
                    <Button onClick={() => setActiveTab('config')}>
                      Configurar API
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <WhatsAppSendMessage />
            )}
          </TabsContent>

          <TabsContent value="templates" className="space-y-6">
            {!isConfigured ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      API Não Configurada
                    </h3>
                    <p className="text-foreground mb-4">
                      Configure a API WhatsApp para gerenciar templates de mensagens.
                    </p>
                    <Button onClick={() => setActiveTab('config')}>
                      Configurar API
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <WhatsAppTemplates />
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            {!isConfigured ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      API Não Configurada
                    </h3>
                    <p className="text-foreground mb-4">
                      Configure a API WhatsApp para visualizar o histórico de mensagens.
                    </p>
                    <Button onClick={() => setActiveTab('config')}>
                      Configurar API
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <WhatsAppHistory />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AdminGuard>
  );
}
